// ---------------------------------------------------------------------------
// Safe DOM helpers — the security backbone of the rewrite.
//
// The original site built markup with template strings + innerHTML, so any
// user-controlled value (player names, team names) could inject HTML/JS. Here
// we NEVER assign untrusted data via innerHTML. `el()` builds nodes and sets
// text via textContent, so values are always treated as data, not markup.
// ---------------------------------------------------------------------------

export function el(tag, props = {}, children = []) {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(props)) {
    if (v == null || v === false) continue;
    if (k === "class") node.className = v;
    else if (k === "text") node.textContent = v; // escaped by definition
    else if (k === "html") throw new Error("Refusing raw html — use 'text'.");
    else if (k.startsWith("on") && typeof v === "function") {
      node.addEventListener(k.slice(2).toLowerCase(), v);
    } else if (k === "dataset") {
      Object.assign(node.dataset, v);
    } else {
      node.setAttribute(k, v);
    }
  }
  for (const child of [].concat(children)) {
    if (child == null) continue;
    node.append(child.nodeType ? child : document.createTextNode(String(child)));
  }
  return node;
}

export function clear(node) {
  while (node.firstChild) node.removeChild(node.firstChild);
}

// Material symbol span
export function icon(name, extraClass = "") {
  return el("span", { class: `material-symbols-outlined ${extraClass}`.trim(), text: name });
}

// ---------------------------------------------------------------------------
// Scoring — identical rules to the backend's intent: exact = 3, tendency = 1.
// ---------------------------------------------------------------------------

export function tendency(home, away) {
  const diff = Number(home) - Number(away);
  if (diff > 0) return "H";
  if (diff < 0) return "A";
  return "D";
}

export function calculatePoints(tip, result) {
  if (!result || result.home_goals == null || result.away_goals == null) return 0;
  const th = Number(tip.home_goals), ta = Number(tip.away_goals);
  const rh = Number(result.home_goals), ra = Number(result.away_goals);
  if (th === rh && ta === ra) return 3;
  if (tendency(th, ta) === tendency(rh, ra)) return 1;
  return 0;
}

export function isLocked(match) {
  return new Date() >= new Date(match.kickoff);
}

export function formatDate(iso) {
  return new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
}

// 3-letter code for the round badges in the design (e.g. "BRA").
export function teamCode(name) {
  return (name || "")
    .normalize("NFD").replace(/[̀-ͯ]/g, "") // strip accents
    .replace(/[^a-zA-Z ]/g, "")
    .slice(0, 3).toUpperCase() || "—";
}

export const FLAGS = {
  "Schweiz": "🇨🇭", "Deutschland": "🇩🇪", "Brasilien": "🇧🇷", "Argentinien": "🇦🇷",
  "Marokko": "🇲🇦", "Mexiko": "🇲🇽", "USA": "🇺🇸", "Kanada": "🇨🇦", "England": "🏴",
  "Frankreich": "🇫🇷", "Spanien": "🇪🇸", "Portugal": "🇵🇹", "Belgien": "🇧🇪",
  "Niederlande": "🇳🇱", "Japan": "🇯🇵", "Südkorea": "🇰🇷", "Australien": "🇦🇺",
  "Türkei": "🇹🇷", "Kroatien": "🇭🇷", "Österreich": "🇦🇹", "Schweden": "🇸🇪",
  "Norwegen": "🇳🇴", "Kolumbien": "🇨🇴", "Ecuador": "🇪🇨", "Uruguay": "🇺🇾",
  "Paraguay": "🇵🇾", "Panama": "🇵🇦", "Ghana": "🇬🇭", "Senegal": "🇸🇳",
  "Algerien": "🇩🇿", "Ägypten": "🇪🇬", "Iran": "🇮🇷", "Irak": "🇮🇶",
  "Saudi-Arabien": "🇸🇦", "Neuseeland": "🇳🇿", "Tunesien": "🇹🇳", "Jordanien": "🇯🇴",
  "Katar": "🇶🇦", "Südafrika": "🇿🇦", "Tschechien": "🇨🇿", "Bosnien und Herzegowina": "🇧🇦",
  "Kap Verde": "🇨🇻", "Usbekistan": "🇺🇿", "DR Kongo": "🇨🇩", "Elfenbeinküste": "🇨🇮",
  "Haiti": "🇭🇹", "Schottland": "🏴", "Curaçao": "🇨🇼",
};

export function flagFor(team) {
  return FLAGS[team] || "🏳️";
}
